import React, { useState, useEffect } from "react";
import { useAdminGetTransactionsQuery } from "../../services/organization/subscription";
import moment from "moment";
import EmptyState from "../EmptyState";
import Icon from "../icons/Icon";
import { TransactionsIcon } from "../icons/Icons";
import { useTranslation } from "react-i18next";
import Pagination from "../Tables/Pagination";
import TransactionStatus from "./TransactionStatus";
import { formatCurrency } from "../../utils/currency";

const TransactionsTab = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useAdminGetTransactionsQuery({ page }, { refetchOnMountOrArgChange: true });
  const transactions = data?.transactions || [];
  const pagination = data?.metadata?.pagination;

  const { t, i18n } = useTranslation(["subscription"]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination?.pages) {
      setPage(newPage);
    }
  };

  return (
    <div className="sub-tab-transactions">
      {/* Table */}
      <div className="transactions-table-wrapper">
        <table className="transactions-table">
          <thead>
            <tr>
              <th className="subscription-plan">{t("transactionsTable.plan")}</th>
              <th className="amount">{t("transactionsTable.amount")}</th>
              <th className="status">{t("transactionsTable.status")}</th>
              <th>{t("transactionsTable.paymentProvider")}</th>
              <th>{t("transactionsTable.intentId")}</th>
              <th>{t("transactionsTable.receiptId")}</th>
              <th>{t("transactionsTable.trackId")}</th>
              <th className="paid-at">{t("transactionsTable.paidAt")}</th>
              <th className="created-at">{t("transactionsTable.createdAt")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="9" className="py-5 text-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : transactions.length > 0 ? (
              transactions.map((txn) => (
                <tr key={txn.id}>
                  <td className="txn-item subscription-plan">
                    <div className="txn-item-cell">
                      <span className="txn-cart-icon">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="9" cy="21" r="1" />
                          <circle cx="20" cy="21" r="1" />
                          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                        </svg>
                      </span>
                      <div>
                        {/* Show plan name if available, otherwise fallback to transaction info */}
                        <p className="txn-title">
                          {txn.subscription?.plan_name || `Transaction #${txn?.id}`}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="txn-total amount">
                    {formatCurrency(txn?.amount, txn?.currency || "KWD", i18n.language === "ar" ? "ar-KW" : "en-KW")}
                  </td>
                  <td className="status">
                    <TransactionStatus status={txn?.status} reason={txn?.failure_reason} />
                  </td>
                  <td className="txn-payment">
                    {txn?.payment_provider?.charAt(0).toUpperCase() + txn?.payment_provider?.slice(1)}
                  </td>
                  <td>
                    {txn?.provider_payment_intent_id}
                  </td>
                  <td>{txn?.tap_receipt_id || "-"}</td>
                  <td>{txn?.tap_track_id || "-"}</td>
                  <td className="paid-at">
                    {txn?.paid_at ? moment(txn?.paid_at).locale(i18n.language).format("MMM DD, YYYY hh:mm A") : "-"}
                  </td>
                  <td className="txn-date created-at">
                    {moment(txn?.created_at).locale(i18n.language).format("MMM DD, YYYY hh:mm A")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="txn-empty">
                  <EmptyState
                    iconComponent={
                      <Icon
                        icon={<TransactionsIcon />}
                        iconName="users"
                        iconSize={38}
                        viewBox="0 0 25 25"
                        iconColor="#bbbabf"
                        hasStroke
                      />
                    }
                    title={t("noTransactionsTitle")}
                    emptyStateText={t("noTransactionsSubtitle")}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <Pagination
          current={pagination?.current}
          pages={pagination?.pages}
          handlePagination={handlePageChange}
        />
      )}
    </div>
  );
};

export default TransactionsTab;
